<script lang="ts" setup>
import { nextTick, ref } from "vue"
import { useI18n } from "vue-i18n"
import BorneRow from "@modules/irve/components/form/borne/BorneRow.vue"
import { useVModel } from "@vueuse/core"
import { IrveFormBorne } from "@types"

type Props = {
    bornes: IrveFormBorne[]
}

const props = withDefaults(defineProps<Props>(), {
    bornes: () => [],
})

const emit = defineEmits(["update:bornes"])
/**
 * Liste des bornes disponibles
 */
const modelBornes = useVModel(props, "bornes", emit, {
    // utilise un watch pour surveiller les changements dans la liste des périodes sélectionnées
    passive: true,
    // Surveille les changements profonds dans la liste des périodes sélectionnées pour déclencher le emit "update:modelValue"
    deep: true,
    eventName: "update:bornes",
    // il faut cloner la props pour éviter de crée un problème de props mutation.
    // Comme la valeur est un tableau, la référence est passée et les modifications sont répercutées. En clonant, on évite ce problème.
    clone: true,
    defaultValue: [],
})

/**
 * Liste des références des composants BorneRow
 */
const borneRow = ref<(typeof BorneRow)[] | null>(null)

/**================================================================*/
/*                         Actions
==================================================================*/
/**
 * Ajoute une borne à la liste
 */
function onClickAddRow() {
    modelBornes.value.push({
        power: null,
        powerFactor: null,
        powerMean: null,
        quantity: 1,
    })
    emit("update:bornes", modelBornes.value)
}
/**
 * Supprime une borne de la liste
 * @param index - index de la borne à supprimer
 */
function onRemoveBorne(index: number) {
    modelBornes.value.splice(index, 1)
    emit("update:bornes", modelBornes.value)
}

/**
 * Duplique une borne de la liste
 * @param item - borne à dupliquer
 */
function onDuplicateBorne(item: IrveFormBorne) {
    modelBornes.value.push({
        power: item.power,
        powerFactor: item.powerFactor,
        powerMean: item.powerMean,
        quantity: item.quantity,
    })
    // validate le dernier élément ajouté
    nextTick(() => {
        if (!borneRow.value) return
        borneRow.value[modelBornes.value.length - 1].validate()
        emit("update:bornes", modelBornes.value)
    })
}

/**
 * Modifie une borne de la liste
 * @param item - borne modifiée
 * @param index - index de la borne modifiée
 */
function onUpdateBorne(item: IrveFormBorne, index: number) {
    modelBornes.value.splice(index, 1, item)
    emit("update:bornes", modelBornes.value)
}

/**================================================================*/
/*                         Lang
==================================================================*/
const { t } = useI18n({
    messages: {
        fr: {
            add: "Nouvelle Borne",
            info_message:
                "Renseignez les bornes de recharge prévues dans l’installation.",
        },
    },
})

defineExpose({
    validate: () => {
        if (!borneRow.value) return
        let valid = true
        for (const row of borneRow.value) {
            // si une ligne n'est pas valide, on retourne false
            if (!row.validate()) valid = false
        }
        return valid
    },
})
</script>
<template>
    <div class="panel-borne flex flex-col gap-6">
        <BaseMessage class="panel-borne__errors" severity="info">
            {{ t("info_message") }}</BaseMessage
        >
        <div class="panel-borne-form flex flex-col">
            <div
                v-for="(item, index) in modelBornes"
                :key="index"
                class="flex flex-col gap-4"
            >
                <BorneRow
                    :index="index + 1"
                    :borne="item"
                    ref="borneRow"
                    @update:borne="onUpdateBorne($event, index)"
                    @click:remove="onRemoveBorne(index)"
                    @click:duplicate="onDuplicateBorne(item)"
                ></BorneRow>
                <BaseDivider
                    v-if="index < modelBornes.length - 1"
                ></BaseDivider>
            </div>
        </div>
        <slot name="error"></slot>
        <!-- Bouton d'ajout -->
        <BaseButton
            :label="t('add')"
            icon="pi pi-plus"
            severity="secondary"
            class="mx-auto"
            @click="onClickAddRow"
        ></BaseButton>
    </div>
</template>
